import React from "react";
import { MessageCircle } from "lucide-react";
import { timeAgo } from "@/lib/api";
import MemberAvatar from "@/components/common/MemberAvatar";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";

/**
 * Conversation list sidebar for the Messages page.
 *
 * Extracted verbatim from src/pages/Messages.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged.
 */
export default function ConversationList({
  activeId,
  list,
  loadingList,
  navigate,
  setParams,
}) {
  return (
        <aside className={`sm-card bg-white overflow-hidden ${activeId ? "hidden lg:block" : ""}`}>
          <div className="px-4 py-3 border-b border-[#E3DED4] bg-[#F7F4EF]">
            <p className="text-sm font-bold text-[#1F2A2E]">
              Conversations{" "}
              {list.total_unread > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-[#D07A4A] text-white text-xs">
                  {list.total_unread}
                </span>
              )}
            </p>
          </div>
          {loadingList ? (
            <Spinner label="Loading conversations" />
          ) : list.conversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageCircle className="w-8 h-8 mx-auto text-[#BFD9DB]" />
              <p className="mt-3 text-sm font-semibold text-[#1F2A2E]">No conversations yet</p>
              <p className="mt-1 text-xs text-[#4B5A60] leading-5">
                Send a sponsorship request from a sponsor profile to get started.
              </p>
              <Button
                onClick={() => navigate("/sponsors")}
                data-testid="messages-browse-cta"
                className="mt-4 bg-[#1F6F78] hover:bg-[#185B62] text-white"
              >
                Browse sponsors
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-[#E3DED4] max-h-[70vh] overflow-y-auto sm-scroll">
              {list.conversations.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setParams({ c: c.id })}
                    data-testid={`conversation-item-${c.id}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-[#F7F4EF] ${
                      c.id === activeId ? "bg-[#D7EEF0]" : "bg-white"
                    }`}
                  >
                    <MemberAvatar user={c.other_user} size={42} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm text-[#1F2A2E] truncate">
                          {c.other_user?.display_name}
                        </p>
                        {c.unread_count > 0 && (
                          <span className="px-1.5 rounded-full bg-[#D07A4A] text-white text-[10px] font-bold">
                            {c.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#4B5A60] truncate">
                        {c.last_message_preview || "No messages yet"}
                      </p>
                      <p className="text-[11px] text-[#7A868B]">{timeAgo(c.last_message_at)}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>
  );
}
